/**
 * Full-text search over every guide this app ships.
 *
 * WHY IT IS A BUILD ARTEFACT AND NOT AN API CALL. The guides are static files beside the app on
 * S3. A search that went through the API would stop working in exactly the circumstance somebody
 * opens the documentation to get out of -- and it would need an index of content the backend does
 * not otherwise hold. scripts/build-docs-index.js writes search-index.json from the same parse it
 * uses for the subject index, so the two cannot describe different documentation.
 *
 * FETCHED ON FIRST USE, NOT ON MOUNT. The index is ~80KB over the wire. Most visits to this page
 * are somebody going to a guide they already know, and they should not pay for a search they did
 * not run. The first keystroke fetches it; the browser caches it after that.
 *
 * RESULTS ARE SECTIONS, NOT PAGES. A hit on a 300-line chapter that opens it at the top has told
 * the reader the answer is in there somewhere. Every result here carries its section's anchor, so
 * the guide opens on the paragraph that matched.
 */
import React from "react";
import { Box, CircularProgress, InputAdornment, TextField, Typography } from "@mui/material";
import { SearchOutlined, CloseOutlined } from "@mui/icons-material";

import { Blue3, Grey2, Grey6, Grey7, White } from "../../theme/styles";

type Record = {
    f: string;   // file
    i: string;   // section id
    t: string;   // page title
    s: string;   // page locator, e.g. "P3 Deciding"
    n: string;   // section number
    h: string;   // section heading
    x: string;   // section text
};

type Hit = { record: Record; score: number; snippet: React.ReactNode };

const LIMIT = 12;
const SNIPPET_CHARS = 190;

/** Split a query into terms. Quoted phrases stay whole, so "link map" can be asked for exactly. */
function terms(query: string): string[] {
    const found: string[] = [];
    const re = /"([^"]+)"|(\S+)/g;
    let m;
    while ((m = re.exec(query)) !== null) {
        const term = (m[1] ?? m[2]).toLowerCase().trim();
        if (term.length > 1) found.push(term);
    }
    return found;
}

/** A window of text around the first match, with the matched terms marked.
 *
 *  SHOWN FROM THE MATCH, NOT FROM THE START. A snippet that always began at the section's first
 *  sentence would be the same three lines for every result in that section, and would not show the
 *  reader why this result came back. */
function snippetFor(text: string, words: string[]): React.ReactNode {
    const lower = text.toLowerCase();
    let at = -1;
    for (const word of words) {
        const found = lower.indexOf(word);
        if (found !== -1 && (at === -1 || found < at)) at = found;
    }
    const start = at <= 60 ? 0 : at - 60;
    const slice = text.slice(start, start + SNIPPET_CHARS);
    const body = (start > 0 ? "…" : "") + slice + (start + SNIPPET_CHARS < text.length ? "…" : "");

    // Mark every term in the window. Built by splitting rather than by injecting HTML, because
    // this text comes from a file and should never be interpreted as markup.
    const pattern = words
        .map(word => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
        .sort((a, b) => b.length - a.length)
        .join("|");
    if (!pattern) return body;
    const parts = body.split(new RegExp(`(${pattern})`, "ig"));
    return parts.map((part, index) =>
        words.includes(part.toLowerCase())
            ? <mark key={index} style={{backgroundColor: "#fff3bf", padding: 0}}>{part}</mark>
            : <React.Fragment key={index}>{part}</React.Fragment>);
}

function rank(records: Record[], query: string): Hit[] {
    const words = terms(query);
    if (!words.length) return [];

    const scored: Hit[] = [];
    for (const record of records) {
        const heading = record.h.toLowerCase();
        const text = record.x.toLowerCase();
        let score = 0;
        let matched = 0;
        for (const word of words) {
            let here = 0;
            // A term in the heading is what the section is ABOUT; a term in the body is a mention.
            if (heading.includes(word)) here += 12;
            const occurrences = text.split(word).length - 1;
            here += Math.min(occurrences, 6);
            if (here) matched += 1;
            score += here;
        }
        // Every term must appear somewhere, or a two-word query returns everything matching either.
        if (matched < words.length) continue;
        scored.push({ record, score, snippet: snippetFor(record.x, words) });
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, LIMIT);
}

export default function DocSearch({ open }: { open: (file: string, hash: string) => void }) {
    const [query, setQuery] = React.useState("");
    const [records, setRecords] = React.useState<Record[] | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [failed, setFailed] = React.useState(false);

    const load = React.useCallback(() => {
        if (records || loading || failed) return;
        setLoading(true);
        fetch("/docs/search-index.json")
            .then(response => (response.ok ? response.json() : Promise.reject(response.status)))
            .then(data => setRecords(data.records as Record[]))
            .catch(() => setFailed(true))
            .finally(() => setLoading(false));
    }, [records, loading, failed]);

    const hits = React.useMemo(
        () => (records ? rank(records, query) : []), [records, query]);
    const asking = query.trim().length > 1;

    return (
        <Box sx={{padding: "12px 20px 0", maxWidth: "860px"}}>
            <TextField
                fullWidth size="small" value={query} autoComplete="off"
                placeholder="Search the documentation"
                onFocus={load}
                onChange={event => { load(); setQuery(event.target.value); }}
                onKeyDown={event => {
                    if (event.key === "Escape") setQuery("");
                    if (event.key === "Enter" && hits.length) {
                        open(hits[0].record.f, hits[0].record.i);
                    }
                }}
                slotProps={{input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchOutlined sx={{fontSize: 18, color: Grey6}}/>
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end">
                            {loading ? <CircularProgress size={14}/> : null}
                            {query ? (
                                <CloseOutlined
                                    aria-label="Clear the search"
                                    onClick={() => setQuery("")}
                                    sx={{fontSize: 16, color: Grey6, cursor: "pointer"}}/>
                            ) : null}
                        </InputAdornment>
                    ),
                    sx: {backgroundColor: White, fontSize: 14},
                }}}
            />

            {asking ? (
                <Box sx={{marginTop: "8px", border: `1px solid ${Grey2}`, borderRadius: "4px",
                          backgroundColor: White, maxHeight: "420px", overflowY: "auto"}}>
                    {failed ? (
                        <Typography sx={{fontSize: 13, color: Grey7, padding: "12px 14px"}}>
                            The search index could not be loaded. Every guide is still listed below.
                        </Typography>
                    ) : null}

                    {!failed && !records && loading ? (
                        <Typography sx={{fontSize: 13, color: Grey7, padding: "12px 14px"}}>
                            Loading the index…
                        </Typography>
                    ) : null}

                    {/* A SEARCH THAT FINDS NOTHING SAYS SO, and says what it searched. "No results"
                        with no denominator is the same failure the SBOM product exists to correct. */}
                    {records && !hits.length ? (
                        <Typography sx={{fontSize: 13, color: Grey7, padding: "12px 14px"}}>
                            Nothing in {records.length} sections matches
                            {" "}<b>{query.trim()}</b>. Every word has to appear in the same
                            section; try fewer words.
                        </Typography>
                    ) : null}

                    {hits.map(hit => (
                        <Box
                            key={`${hit.record.f}#${hit.record.i}`}
                            onClick={() => open(hit.record.f, hit.record.i)}
                            sx={{padding: "10px 14px", cursor: "pointer",
                                 borderBottom: `1px solid ${Grey2}`,
                                 "&:last-of-type": {borderBottom: "none"},
                                 "&:hover": {backgroundColor: "#f5f9fc"}}}>
                            <Typography sx={{fontSize: 14, fontWeight: 600, color: Blue3}}>
                                {hit.record.h}
                            </Typography>
                            <Typography sx={{fontSize: 12, color: Grey6, marginBottom: "3px"}}>
                                {hit.record.t}
                                {hit.record.n ? ` · §${hit.record.n}` : ""}
                            </Typography>
                            <Typography sx={{fontSize: 13, color: Grey7, lineHeight: 1.45}}>
                                {hit.snippet}
                            </Typography>
                        </Box>
                    ))}

                    {records && hits.length === LIMIT ? (
                        <Typography sx={{fontSize: 12, color: Grey6, padding: "8px 14px"}}>
                            Showing the {LIMIT} best matches. Add a word to narrow it.
                        </Typography>
                    ) : null}
                </Box>
            ) : null}
        </Box>
    );
}
